import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { Pagination } from "antd";
import { formatYYYY_MM_DD } from "../../utils/dateFormating";
import { useConfirmation } from "../../hooks/useConfirmation";
import { ConfirmationModal } from "../../components/Model/ConfirmationModel";
import { useThunk } from "../../hooks/useThunk";
import { fetchUserPosts, deleteJobPost } from "../../store";
import CustomButton from "../../components/Form/CustomButton";
import showToast from "../../utils/toastNotifications";
import Loader from "../../components/Common/Loader";
import ErrorDisplay from "../../error/ErrorDisplay";
import ClientPostsJobPostCardSkeleton from "../../components/skeleton/ClientPostsJobPostCardSkeleton";

const itemsPerPage = 5;

const ClientPosts = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Local state pagination
  const [currentPage, setCurrentPage] = useState(
    searchParams.get("page") ? parseInt(searchParams.get("page")) : 1
  );

  const { requestConfirmation, confirmation, confirm, cancel } =
    useConfirmation();

  const [doFetchUserPosts, isFetchUserPosts, fetchUserPostsError] =
    useThunk(fetchUserPosts);
  const [doDeleteJobPost, isDeleteJobPost] = useThunk(deleteJobPost);

  const { posts = [], pagination = {} } = useSelector(
    (state) => state.jobPost.myPosts || {}
  );

  // Function to fetch user posts
  const fetchUserPostsData = () => {
    const offset = (currentPage - 1) * itemsPerPage;
    const limit = itemsPerPage;
    doFetchUserPosts({ offset, limit });
  };

  // Track if page was changed
  const [pageChanged, setPageChanged] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      await fetchUserPostsData();

      // Only scroll if the page was changed
      if (pageChanged) {
        window.scrollTo({ top: 0, behavior: "smooth" });
        setPageChanged(false);
      }
    };

    fetchData();
  }, [currentPage, doFetchUserPosts]);

  // Update URL on filters or page changes
  useEffect(() => {
    const params = new URLSearchParams();
    if (currentPage > 1) params.set("page", currentPage.toString());
    setSearchParams(params);
  }, [currentPage, setSearchParams]);

  // Handle page changes
  const handlePageChange = (page) => {
    setPageChanged(true);
    setCurrentPage(page);
  };

  // Delete a post with confirmation
  const handleDelete = async (record) => {
    const isConfirmed = await requestConfirmation({
      type: "delete",
      title: "Delete Post",
      message: "Are you sure you want to delete this post?",
      confirmText: "Yes, Delete",
      cancelText: "Cancel",
    });
    if (isConfirmed) {
      const result = await doDeleteJobPost({ jobPostId: record._id });
      if (result.success) {
        showToast("success", "Job Post Deleted Successfully.");
        // Fetch posts again after successful deletion
        fetchUserPostsData();
      } else {
        showToast("error", result.error?.message || "Failed to delete post.");
      }
    }
  };

  return (
    <div className="w-full mt-12 mb-20 px-[20px] md:px-[40px] lg:px-[100px]">
      <div className="p-4 md:p-9 bg-white border border-light-blue rounded-[20px]">
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between w-full gap-4">
          <h1 className="font-nonito_sans font-bold text-level-2 leading-[43.65px] text-primary">
            My Posts
          </h1>
        </div>

        {/* Show loader when fetching posts */}
        {isFetchUserPosts ? (
          /* Skeleton for job post card */
          <div className="space-y-6 mt-6">
            {[1, 2, 3].map((idx) => (
              <ClientPostsJobPostCardSkeleton key={idx} />
            ))}
          </div>
        ) : fetchUserPostsError ? (
          <ErrorDisplay
            errorMessage={fetchUserPostsError?.message || "An error occurred"}
            buttonText="Try Again"
            onButtonClick={fetchUserPostsData}
          />
        ) : (
          <>
            {/* Job cards */}
            <div className="space-y-6 mt-6">
              {posts?.length > 0 ? (
                posts.map((post) => (
                  <div
                    key={post?._id}
                    className="rounded-lg overflow-hidden border border-light-blue"
                  >
                    <div className="bg-primary px-6 py-3 flex flex-col md:flex-row items-start md:items-center justify-between gap-2">
                      <div>
                        <h2 className="font-inter font-semibold md:text-[15px] leading-5 text-white">
                          {post?.title}
                        </h2>
                      </div>
                      <p className="font-inter font-semibold md:text-[15px] text-white mt-2">
                        {formatYYYY_MM_DD(post?.date)}
                      </p>
                    </div>
                    <div className="bg-white px-6 py-3 flex flex-col md:flex-row justify-between items-start gap-4">
                      <div className="flex flex-col gap-4">
                        <div>
                          <p className="font-inter text-level-6 leading-4 font-semibold mb-3 text-dark">
                            Category
                          </p>
                          <span className="inline-flex items-center px-5 py-3 bg-light rounded-[20px] font-inter text-content text-level-6 leading-4">
                            {post?.category?.categoryItem}
                          </span>
                        </div>
                        <div>
                          <p className="font-inter font-medium text-level-6 text-dark leading-4 -mt-1 mb-3">
                            Skills
                          </p>
                          <div className="flex flex-wrap md:flex-nowrap gap-2">
                            {post?.category?.skills?.map((skill, idx) => (
                              <span
                                key={idx}
                                className="px-5 py-3 items-center font-inter text-level-6 text-content bg-light rounded-[20px] whitespace-nowrap"
                              >
                                {skill}
                              </span>
                            ))}
                          </div>
                        </div>
                      </div>

                      <div className="flex flex-wrap xs:flex-nowrap md:flex-col gap-3 w-full justify-center md:items-end md:mt-6 md:mb-3">
                        <CustomButton
                          buttonText="View Post"
                          bgColor="bg-white"
                          textColor="text-primary"
                          borderColor="border-primary"
                          className="max-w-[150px] min-w-[75px] h-[35px] rounded-[4px] font-inter text-xs xs:text-level-6"
                          onClick={() =>
                            navigate(`/client/view-post-job/${post?._id}`)
                          }
                        />
                        <CustomButton
                          buttonText="Edit Post"
                          bgColor="bg-white"
                          textColor="text-primary"
                          borderColor="border-primary"
                          className="max-w-[150px] min-w-[75px] h-[35px] rounded-[4px] font-inter text-xs xs:text-level-6"
                          onClick={() =>
                            navigate(`/client/edit-post-job/${post?._id}`)
                          }
                        />
                        <CustomButton
                          buttonText="Delete Post"
                          bgColor="bg-white"
                          textColor="text-primary"
                          borderColor="border-primary"
                          className="max-w-[150px] min-w-[75px] h-[35px] rounded-[4px] font-inter text-xs xs:text-level-6"
                          onClick={() => handleDelete(post)}
                          disabled={isDeleteJobPost}
                        />
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-10">
                  <p className=" text-gray-500 font-inter text-level-6">
                    No posts found
                  </p>
                </div>
              )}
            </div>

            {/* Pagination */}
            {posts?.length > 0 && (
              <div className="flex justify-center mt-8">
                <Pagination
                  current={currentPage}
                  total={pagination?.totalDocuments || 0}
                  pageSize={itemsPerPage}
                  onChange={handlePageChange}
                  showSizeChanger={false}
                  className="font-inter"
                />
              </div>
            )}
          </>
        )}

        {/* Show loader for delete operation */}
        {isDeleteJobPost && (
          <div className="fixed inset-0 bg-black bg-opacity-30 flex justify-center items-center z-50">
            <div className="bg-white px-8 py-6 rounded-lg shadow-lg">
              <Loader />
              <p className="text-center mt-4 font-inter">Deleting post...</p>
            </div>
          </div>
        )}

        {/* Delete post modal */}
        {confirmation.isOpen && (
          <ConfirmationModal
            isOpen={confirmation.isOpen}
            options={confirmation.options}
            onConfirm={confirm}
            onCancel={cancel}
          />
        )}
      </div>
    </div>
  );
};

export default ClientPosts;
